'use client'

import { getAuthSessionRevision, getStoredAuthToken, subscribeAuthSession } from '@/lib/authStorage'
import { apiClient, gameApi, authUtils } from '@/lib/api'
import supabase, { isSupabaseConfigured } from '@/lib/supabase'
import { setRecentPlayAccountKey } from '@/utils/recentPlayHistory'
import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useSyncExternalStore } from 'react'

export const AUTH_REQUIRED_EVENT = 'crypto-auth-required'

export const requestAuthentication = (reason = 'protected-menu') => {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent(AUTH_REQUIRED_EVENT, {
      detail: { modalOption: 'login', reason },
    })
  )
}

// User types
// interface User {
//   id: string
//   email: string
//   username?: string
//   isAuthenticated: boolean
//   isLoading: boolean
//   profile: UserProfile | null
//   balance: number
// }

// interface AuthContextType {
//   user: User
//   isLoading: boolean
//   signIn: (email: string, password: string) => Promise<void>
//   signUp: (email: string, password: string, metadata?: { name?: string }) => Promise<void>
//   signInWithGoogle: () => Promise<void>
//   signInWithTwitter: () => Promise<void>
//   signInWithMetaMask: () => Promise<void>
//   signOut: () => Promise<void>
//   refreshProfile: () => Promise<void>
//   updateUser: (profile: Partial<UserProfile>) => void
//   updateBalance: (newBalance: number) => void
//   fetchUserProfile: () => Promise<void>
//   loginWithOTP?: (email: string, code: string) => Promise<void>
// }

const AuthContext = createContext(undefined)

const createGuestUser = (overrides = {}) => ({
  id: '',
  email: '',
  username: '',
  isAuthenticated: false,
  isLoading: false,
  profile: null,
  balance: 0,
  ...overrides,
})

const normalizeProfile = (profile) => {
  if (!profile) return null
  return profile.user || profile.member || profile.profile || profile
}

const getProfileBalance = (profile) =>
  Number(
    profile?.wallet?.available_balance ??
      profile?.balance ??
      profile?.wallet?.balance ??
      profile?.available_balance ??
      profile?.money ??
      0
  )

const createAuthenticatedUser = (profile) => {
  const normalized = normalizeProfile(profile) || {}
  return {
    id: normalized.id || normalized.user_id || normalized.member_id || '',
    email: normalized.email || normalized.user_email || '',
    username:
      normalized.username ||
      normalized.user_unique_id ||
      normalized.nick_name ||
      normalized.user_id ||
      normalized.nickname ||
      normalized.name ||
      normalized.displayName ||
      '',
    authSessionRevision: getAuthSessionRevision(),
    isAuthenticated: true,
    isLoading: false,
    profile: normalized,
    balance: getProfileBalance(normalized),
  }
}

export function AuthProvider({ children }) {
  const sessionRevision = useSyncExternalStore(subscribeAuthSession, getAuthSessionRevision, getAuthSessionRevision)
  const [isLoading, setIsLoading] = useState(false)
  const [user, setUser] = useState(createGuestUser({ isLoading: true }))

  // Flag to prevent multiple simultaneous auth checks
  const authCheckInProgress = useRef(false)
  const signUpInProgress = useRef(false)

  // Fetch user profile (assumes user is already authenticated)
  const fetchUserProfile = useCallback(async ({ clearOnError = true, silent = false } = {}) => {
    const revision = getAuthSessionRevision(), token = getStoredAuthToken()
    const current = () => revision === getAuthSessionRevision() && token === getStoredAuthToken()
    try {
      const response = await gameApi.user.getProfile({ isCurrent: current })
      if (!current()) return null
      if (response.success && response.data) {
        const profile = normalizeProfile(response.data)
        setUser(createAuthenticatedUser(profile))
        if (!silent) {
          console.log('✅ User profile loaded:', profile?.username || profile?.user_id)
        }
        return profile
      } else {
        throw new Error('Failed to fetch profile')
      }
    } catch (error) {
      if (!current()) return null
      if (!silent) {
        console.error('Failed to fetch user profile:', error)
      }
      if (clearOnError) {
        setUser(createGuestUser())
      }
      return null
    }
  }, [])

  // Check authentication status
  const checkAuthStatus = useCallback(async () => {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress.current) {
      console.log('🔄 Auth check already in progress, skipping')
      return
    }

    // Check if we're in a redirect loop - if so, don't attempt auth check
    if (typeof window !== 'undefined' && sessionStorage.getItem('auth_redirect_flag')) {
      console.log('🔄 Auth redirect flag detected, skipping auth status check')
      sessionStorage.removeItem('auth_redirect_flag')
      sessionStorage.removeItem('logout_in_progress') // Clear logout flag as well
      setUser(createGuestUser())
      return
    }

    const revision = getAuthSessionRevision(), token = getStoredAuthToken()
    const current = () => revision === getAuthSessionRevision() && token === getStoredAuthToken()
    try {
      authCheckInProgress.current = true
      setIsLoading(true)

      console.log('🔐 Starting auth profile bootstrap')
      // `/member/id` is the bootstrap/profile request itself. A separate auth
      // probe first would issue the same request twice.
      const profile = await fetchUserProfile({ silent: true })
      if (!current()) return

      if (profile) {
        console.log('✅ User profile bootstrap completed')
      } else {
        // User is not authenticated or the bootstrap profile is unavailable.
        console.log('❌ User is not authenticated')
        setUser(createGuestUser())
      }
    } catch (error) {
      if (!current()) return
      console.error('Auth status check failed:', error)
      setUser(createGuestUser())
    } finally {
      setIsLoading(false)
      authCheckInProgress.current = false
    }
  }, [fetchUserProfile])

  // Sign in with email/password
  const signIn = useCallback(async (userUniqueId, password, captchaToken) => {
    try {
      setIsLoading(true)

      if (isSupabaseConfigured) {
        const { error } = await supabase.auth.signInWithPassword({
          email: userUniqueId,
          password
        })

        if (error) throw error
        return
      }

      const result = await authUtils.login({ user_unique_id: userUniqueId, password }, captchaToken)

      if (!result.success) {
        throw new Error(result.error || result.message || 'Login failed')
      }

      if (result.data) {
        setUser(createAuthenticatedUser(result.data))
      }

      try {
        await fetchUserProfile()
      } catch (profileError) {
        if (!result.data) throw profileError
      }

      // Token exchange will be handled automatically by onAuthStateChange
      // when the SIGNED_IN event is triggered
      return result
    } catch (error) {
      console.error('Sign in error:', error)
      throw new Error(error.message || 'Sign in failed')
    } finally {
      setIsLoading(false)
    }
  }, [fetchUserProfile])

  // Sign up with email/password
  const signUp = useCallback(async (userUniqueId, password, metadata, captchaToken, options = {}) => {
    try {
      setIsLoading(true)

      if (isSupabaseConfigured) {
        signUpInProgress.current = true
        try {
          const { data, error } = await supabase.auth.signUp({
            email: userUniqueId,
            password,
            options: {
              data: metadata
            }
          })

          if (error) throw error
          if (data?.session) {
            const { error: signOutError } = await supabase.auth.signOut()
            if (signOutError) throw signOutError
          }
          if (options.signal?.aborted) return null
          setUser(createGuestUser())
          return data
        } finally {
          signUpInProgress.current = false
        }
      }

      const payload = { ...(metadata || {}) }
      delete payload.nickname
      const result = await authUtils.register({
        user_unique_id: payload.user_unique_id || userUniqueId,
        password,
        ...payload,
      }, captchaToken, options)

      if (options.signal?.aborted) return null
      if (!result.success) {
        throw new Error(result.error || result.message || 'Sign up failed')
      }

      // Registration is an approval request. A successful response must not
      // create an authenticated frontend session.
      setUser(createGuestUser())
      return result
    } catch (error) {
      throw new Error(error.message || 'Sign up failed')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Login with OTP
  const loginWithOTP = useCallback(async (email, code) => {
    try {
      setIsLoading(true)
      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important: needed to receive cookies
        body: JSON.stringify({ email, code })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'OTP verification failed')
      }

      // Store tokens
      if (data.token) {
        localStorage.setItem('auth_token', data.token)
      }
      if (data.platformToken) {
        localStorage.setItem('platform_token', data.platformToken)
      }

      // Update user state with the returned user data
      if (data.user) {
        setUser({
          id: data.user.id,
          email: data.user.email,
          username: data.user.username,
          authSessionRevision: getAuthSessionRevision(),
    isAuthenticated: true,
          isLoading: false,
          profile: data.user,
          balance: data.user.balance
        })
      }

      console.log('✅ OTP login successful')
    } catch (error) {
      console.error('OTP login error:', error)
      throw new Error(error.message || 'OTP verification failed')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Sign in with Google
  const signInWithGoogle = useCallback(async () => {
    try {
      setIsLoading(true)
      if (!isSupabaseConfigured) {
        throw new Error('Social login is not configured for this frontend.')
      }
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) throw error
    } catch (error) {
      console.error('Google sign in error:', error)
      throw new Error(error.message || 'Google sign in failed')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Sign in with Twitter
  const signInWithTwitter = useCallback(async () => {
    try {
      setIsLoading(true)
      if (!isSupabaseConfigured) {
        throw new Error('Social login is not configured for this frontend.')
      }
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'twitter',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) throw error
    } catch (error) {
      console.error('Twitter sign in error:', error)
      throw new Error(error.message || 'Twitter sign in failed')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Sign in with MetaMask
  const signInWithMetaMask = useCallback(async (walletAddress) => {
    try {
      setIsLoading(true)
      console.log('🔐 MetaMask wallet connected:', walletAddress)

      // Step 1: Get nonce from backend
      const nonceResponse = await apiClient.post('/auth/wallet/nonce', {
        walletAddress
      })

      if (!nonceResponse.data?.nonce || !nonceResponse.data?.message) {
        throw new Error('Failed to get authentication nonce')
      }

      const { message } = nonceResponse.data

      // Step 2: Sign the message with MetaMask
      console.log('📝 Requesting signature from MetaMask...')
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, walletAddress]
      })

      if (!signature) {
        throw new Error('Signature request was rejected')
      }

      console.log('✅ Message signed successfully')

      // Step 3: Verify signature and login
      const verifyResponse = await apiClient.post('/auth/wallet/verify', {
        walletAddress,
        signature
      })

      if (verifyResponse.data?.success && verifyResponse.data?.user) {
        // Update user state
        const userData = verifyResponse.data.user
        setUser(createAuthenticatedUser(userData))

        console.log('✅ MetaMask authentication successful:', userData.username)
      } else {
        throw new Error('Verification failed')
      }
    } catch (error) {
      console.error('MetaMask sign in error:', error)

      // User-friendly error messages
      if (error.code === 4001) {
        throw new Error('MetaMask signature request was rejected')
      } else if (error.message?.includes('not installed')) {
        throw new Error('MetaMask is not installed. Please install the MetaMask extension.')
      } else {
        throw new Error(error.message || 'MetaMask authentication failed')
      }
    } finally {
      setIsLoading(false)
    }
  }, [fetchUserProfile])

  // Exchange Supabase token for platform JWT
  const exchangeToken = useCallback(async (supabaseToken) => {
    try {
      // Check if we're in a redirect loop - if so, don't attempt token exchange
      if (typeof window !== 'undefined' && sessionStorage.getItem('auth_redirect_flag')) {
        console.log('🔄 Auth redirect flag detected, skipping token exchange')
        sessionStorage.removeItem('auth_redirect_flag')
        sessionStorage.removeItem('logout_in_progress') // Clear logout flag as well
        setUser(createGuestUser())
        return
      }

      const response = await apiClient.post('/auth/exchange', {
        token: supabaseToken
      })

      if (response.data.success) {
        console.log('✅ Platform token obtained and stored in cookie')
        // Fetch user profile after successful token exchange
        await fetchUserProfile()
      } else {
        throw new Error('Token exchange failed')
      }
    } catch (error) {
      console.error('❌ Token exchange failed:', error)
      // Clear user state on token exchange failure
      setUser(createGuestUser())
      // Ensure Supabase session and local tokens are cleared to avoid repeated failures
      try {
        if (isSupabaseConfigured) {
          await supabase.auth.signOut()
        }
      } catch (_) {}
      if (typeof window !== 'undefined') {
        try {
          // Supabase stores session under this key pattern in localStorage
          localStorage.removeItem('supabase.auth.token')
          // Clear any app-specific tokens if present
          localStorage.removeItem('auth_token')
          localStorage.removeItem('platform_token')
          sessionStorage.removeItem('auth_redirect_flag')
          sessionStorage.removeItem('logout_in_progress')
        } catch (_) {}
      }
      throw error
    }
  }, [fetchUserProfile])

  // Sign out
  const signOut = useCallback(async () => {
    try {
      setIsLoading(true)
      setUser(createGuestUser())

      // Call backend logout to clear cookie
      await authUtils.logout()
      if (getStoredAuthToken()) return

      // Sign out from Supabase
      if (isSupabaseConfigured) {
        await supabase.auth.signOut()
      }

      // Clear user state
      if (getStoredAuthToken()) return
      setUser(createGuestUser())

      console.log('✅ User signed out successfully')

      // Redirect to home page
      if (typeof window !== 'undefined') {
        window.location.href = '/'
      }
    } catch (error) {
      console.error('Sign out error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Refresh user profile
  const refreshProfile = useCallback(async () => {
    if (user.isAuthenticated) {
      await fetchUserProfile()
    }
  }, [user.isAuthenticated, fetchUserProfile])

  // Update user profile
  const updateUser = useCallback((profile) => {
    setUser(prev => ({
      ...prev,
      profile: prev.profile ? { ...prev.profile, ...profile } : null
    }))
  }, [])

  // Update user balance
  const updateBalance = useCallback((newBalance) => {
    setUser(prev => ({
      ...prev,
      balance: newBalance,
      profile: prev.profile ? { ...prev.profile, balance: newBalance } : null
    }))
  }, [])

  // Listen to auth state changes
  useEffect(() => {
    if (!isSupabaseConfigured) {
      return undefined
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state change:', event, session?.user?.email)

        if (event === 'SIGNED_IN' && signUpInProgress.current) {
          setUser(createGuestUser())
          return
        }

        // Check if we're in a redirect loop - if so, don't process auth state changes
        if (typeof window !== 'undefined' && sessionStorage.getItem('auth_redirect_flag')) {
          console.log('🔄 Auth redirect flag detected, skipping auth state change processing')
          return
        }

        if ((event === 'SIGNED_IN' && session?.user) || (event === 'INITIAL_SESSION' && session?.user)) {
          // Exchange Supabase token for platform JWT
          if (session.access_token) {
            try {
              await exchangeToken(session.access_token)
            } catch (error) {
              console.error('❌ Failed to exchange token:', error)
            }
          }
        } else if (event === 'SIGNED_OUT') {
          // Clear user state
          setUser(createGuestUser())
        }
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [exchangeToken])

  // Check auth status on mount only
  useEffect(() => {
    checkAuthStatus()
  }, []) // Empty dependency array - only run once on mount

  useEffect(() => {
    setRecentPlayAccountKey(user.isAuthenticated ? user : null)
  }, [user])

  const value = {
    user: user.isAuthenticated && user.authSessionRevision !== sessionRevision ? createGuestUser() : user,
    isLoading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithTwitter,
    signInWithMetaMask,
    signOut,
    refreshProfile,
    updateUser,
    updateBalance,
    fetchUserProfile,
    loginWithOTP
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth({ optional = false } = {}) {
  const context = useContext(AuthContext)
  if (context === undefined) {
    if (optional) {
      return {
        user: createGuestUser(),
        isLoading: false,
      }
    }
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
